import React from "react";
import AppConstants from "../../constants/AppConstants";
import PropertyMaps from "../../constants/PropertyMaps";
import StyleUtils from "../../utils/StyleUtils";

const { DEFAULT_BORDER_PROPERTIES, BORDER_CIRCLE_RAD } = AppConstants;

const getGradientStyle = (elemProps, theme) =>
  elemProps.get("fillType") === "gradient"
    ? {
        ...StyleUtils.getGradientStyle(elemProps, theme),
        backgroundOrigin: "border-box",
        backgroundColor: "transparent",
      }
    : {};

/**
 * Infers border style for legacy data that has bW/bC but no bS.
 * Legacy elements may have border width and color without an explicit style.
 * @param {Object} params - Border property values
 * @param {number|undefined} params.borderWidth - The border width value
 * @param {string|undefined} params.borderColor - The border color value
 * @param {string|undefined} params.borderStyle - The explicit border style value
 * @returns {string|undefined} The inferred or explicit border style
 */
const inferBorderStyle = ({ borderWidth, borderColor, borderStyle }) => {
  if (borderStyle !== undefined) return borderStyle;

  // Legacy inference: if bW or bC exist, assume solid border
  if ((borderWidth !== undefined && borderWidth > 0) || borderColor !== undefined) {
    return "solid";
  }
  return undefined;
};

/**
 * Resolves border styles, applying defaults when a border style is set.
 * Includes legacy data inference for elements with bW/bC but no bS.
 */
const resolveBorderStyle = ({ borderWidth, borderColor, borderStyle }) => {
  const resolvedStyle = inferBorderStyle({ borderWidth, borderColor, borderStyle });

  if (!resolvedStyle || resolvedStyle === "none") return {};

  return {
    borderWidth: borderWidth ?? DEFAULT_BORDER_PROPERTIES.width,
    borderColor: borderColor ?? DEFAULT_BORDER_PROPERTIES.color,
    borderStyle: resolvedStyle,
    borderRadius: "inherit",
  };
};

/**
 * When the element has advanced fill (backdrop filter), we need the same div to have
 * border-radius and overflow hidden so the blur is clipped to the shape (e.g. oval).
 */
const getAdvancedFillContainerStyle = (elemProps, advancedFillStyle) => {
  if (!advancedFillStyle || !advancedFillStyle.backdropFilter) return {};
  const rad = elemProps.getIn(["border", "rad"]);
  const opacity = Number(elemProps.get("o", 100) ?? 100) / 100;
  const blur = Number(elemProps.get("blur", 0) ?? 0);
  const style = {
    overflow: "hidden",
    opacity,
  };
  if (rad !== undefined) {
    style.borderRadius =
      rad === BORDER_CIRCLE_RAD
        ? BORDER_CIRCLE_RAD
        : String(rad)
            .split(" ")
            .map((v) => (v === "0" ? "0" : `${v}px`))
            .join(" ");
  }
  if (blur > 0) {
    style.filter = `blur(${blur}px)`;
  }
  return style;
};

const Simple = ({ elemProps, theme, inheritShadow = false }) => {
  const inlineStyle = StyleUtils.getStyle(PropertyMaps.STYLE_PROPS_INLINE, elemProps, theme);
  const gradientStyle = getGradientStyle(elemProps, theme);
  const advancedFillStyle = StyleUtils.getAdvancedFillStyle(elemProps);
  const advancedFillClipStyle = getAdvancedFillContainerStyle(elemProps, advancedFillStyle);

  const { borderWidth, borderColor, borderStyle, ...restInlineStyle } = inlineStyle;
  const resolvedBorder = resolveBorderStyle({
    borderWidth,
    borderColor,
    borderStyle,
  });
  const hasBorder = Boolean(resolvedBorder.borderStyle);

  const fillStyle = {
    width: "100%",
    height: "100%",
    boxShadow: inheritShadow ? "inherit" : "unset",
    borderRadius: inheritShadow ? "inherit" : "unset",
    ...restInlineStyle,
    ...advancedFillClipStyle,
    ...gradientStyle,
    ...advancedFillStyle,
  };

  if (!hasBorder) {
    return <div data-testid="simple-element" style={fillStyle} />;
  }

  const borderOverlayStyle = {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    backgroundColor: "transparent",
    boxSizing: "border-box",
    ...resolvedBorder,
  };

  return (
    <>
      <div data-testid="simple-element" style={fillStyle} />
      <div data-testid="simple-element-border" style={borderOverlayStyle} />
    </>
  );
};

export default Simple;
